// The `activity-item` route (`/transactions/item/:transactionId`, design
// `#s05` item detail + split editor). The param is `transactionId`
// (execution plan §3/§9.10 param rename from the manifest's old
// `:lineItemId`): the detail is a transaction with N line items, not a
// single line item. Renders `TransactionDetailPanel` full-screen with a
// Close action back to the feed; the same panel is reused as a side panel
// by wide-band feed layouts, per the plan's "share one body component" rule.
import { Link, useLocation, useParams } from 'react-router-dom';
import { Skeleton } from '@moneyskyline/ui';
import { RouteRecoveryScreen } from '@/app/states/RouteStates';
import { useTransaction, type PrimaryTransactionDetail, type TransactionDetail } from '@/api/hooks/useTransaction';
import { TransactionDetailPanel } from './TransactionDetailPanel';
import { useShellContext } from '@/app/shell/AppShell';

const RETURN_CONTEXT_KEYS = new Set(['account', 'from', 'to', 'category', 'merchant']);

function returnContextSearch(search: string): string {
  const query = search.startsWith('?') ? search.slice(1) : search;
  const segments = query.split('&').filter((segment) => {
    if (!segment) return false;
    const rawKey = segment.split('=', 1)[0];
    try {
      return RETURN_CONTEXT_KEYS.has(decodeURIComponent(rawKey.replace(/\+/g, ' ')));
    } catch {
      return false;
    }
  });
  return segments.length > 0 ? `?${segments.join('&')}` : '';
}

function responseStatus(error: unknown): number | undefined {
  if (typeof error !== 'object' || error === null || !('response' in error)) return undefined;
  const response = error.response;
  if (typeof response !== 'object' || response === null || !('status' in response)) return undefined;
  return typeof response.status === 'number' ? response.status : undefined;
}

type CanonicalPrimaryTransactionDetail = Extract<PrimaryTransactionDetail, { primaryKind: 'canonical' | 'canonical_alias' }>;

function isCanonicalPrimaryTransactionDetail(value: PrimaryTransactionDetail | TransactionDetail): value is CanonicalPrimaryTransactionDetail {
  if (!('primaryKind' in value)) return false;
  const primaryKind = value.primaryKind;
  return primaryKind === 'canonical' || primaryKind === 'canonical_alias';
}

export function TransactionDetailPage() {
  const { transactionId } = useParams<{ transactionId: string }>();
  const location = useLocation();
  const { scopeAvailability, retryScopeAvailability } = useShellContext();
  const scopeReady = scopeAvailability === 'ready';
  const query = useTransaction(transactionId, null, scopeReady);
  const returnTarget = `/transactions${returnContextSearch(location.search)}${location.hash}`;

  return (
    <section aria-labelledby="activity-item-heading" className="p-4">
      <h1 id="activity-item-heading" tabIndex={-1} className="sr-only">Transaction detail</h1>
      {!scopeReady ? (
        scopeAvailability === 'pending' ? (
          <p role="status" className="mt-4 rounded-md border border-border bg-muted/30 p-4 text-sm text-muted-foreground">Checking transaction scope availability…</p>
        ) : (
          <p role="status" className="mt-4 rounded-md border border-degraded-border bg-degraded-surface p-4 text-sm text-degraded-foreground">
            Transaction scope availability could not be checked.{' '}
            <button type="button" className="min-h-11 rounded-md border border-input px-3 underline" onClick={retryScopeAvailability}>Retry</button>
          </p>
        )
      ) : query.isPending ? (
        <div data-testid="detail-loading-skeleton">
          <Skeleton className="h-6 w-40" />
          <Skeleton className="mt-3 h-10 w-full" />
          <Skeleton className="mt-3 h-32 w-full" />
        </div>
      ) : query.isError ? (
        responseStatus(query.error) === 404
          ? <RouteRecoveryScreen kind="not-found" frame="inline" />
          : <RouteRecoveryScreen kind="api-unavailable" frame="inline" />
      ) : !query.data || typeof query.data !== 'object' || !('primaryKind' in query.data) ? (
        <RouteRecoveryScreen kind="api-unavailable" frame="inline" />
      ) : query.data.primaryKind === 'initialization_required_physical' ? (
        <p role="status" className="mt-4 rounded-md border border-degraded-border bg-degraded-surface p-4 text-sm text-degraded-foreground">Transaction history needs setup. Detail is unavailable.</p>
      ) : !isCanonicalPrimaryTransactionDetail(query.data) ? (
        <RouteRecoveryScreen kind="api-unavailable" frame="inline" />
      ) : (
        <div data-testid="transaction-detail-reading-column" className="mx-auto w-full max-w-2xl min-w-0">
          <div className="flex items-center justify-between">
            <Link to={returnTarget} className="inline-flex min-h-11 min-w-11 items-center text-sm text-link">
              Close
            </Link>
          </div>
          <TransactionDetailPanel txn={query.data} />
        </div>
      )}
    </section>
  );
}

export default TransactionDetailPage;
